import React, { useState } from "react";

import {
  App,
  Button,
  InputNumber,
  Modal,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

import {
  deliverKermesPreorder,
  cancelKermesPreorder,
} from "../utils/api";

import { PRODUCT_NAMES } from "../constants";

import { formatMoney, getStatusConfig } from "../utils";

const { Text } = Typography;

export default function PreordersTable({
  orders,
  loading,
  onChanged,
  userId,
}) {
  const { message, modal } = App.useApp();

  const [payment, setPayment] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deliverModalOpen, setDeliverModalOpen] = useState(false);
  const [delivering, setDelivering] = useState(false);

  const remaining = Number(selectedOrder?.remaining || 0);

  const paymentCents = Math.round(Number(payment || 0) * 100);
  const remainingCents = Math.round(remaining * 100);

  const changeCents = Math.max(
    0,
    paymentCents - remainingCents
  );

  const change = changeCents / 100;

  const canDeliver =
    selectedOrder &&
    paymentCents >= remainingCents &&
    !delivering;

  const openDeliverModal = (order) => {
    setSelectedOrder(order);
    setPayment(0);
    setDeliverModalOpen(true);
  };

  const closeDeliverModal = () => {
    if (delivering) return;

    setDeliverModalOpen(false);
    setSelectedOrder(null);
    setPayment(0);
  };

  const confirmDelivery = async () => {
    if (!selectedOrder) {
      return;
    }

    if (paymentCents < remainingCents) {
      message.error("El efectivo recibido es insuficiente.");
      return;
    }

    setDelivering(true);

    try {
      await deliverKermesPreorder(selectedOrder.id);

      message.success("Pedido marcado como entregado.");

      setDeliverModalOpen(false);
      setSelectedOrder(null);
      setPayment(0);

      onChanged();
    } catch (error) {
      message.error(error.message);
    } finally {
      setDelivering(false);
    }
  };

  const cancelOrder = (order) => {
    modal.confirm({
      title: `Cancelar pedido #${order.id}`,
      content:
        "El pedido se conservará en el historial y las cantidades serán devueltas al inventario.",
      okText: "Cancelar pedido",
      okButtonProps: { danger: true },
      cancelText: "Cerrar",

      onOk: async () => {
        try {
          await cancelKermesPreorder(order.id, userId);

          message.success(
            "Pedido cancelado e inventario restaurado."
          );

          onChanged();
        } catch (error) {
          message.error(error.message);
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 70,
    },

    {
      title: "Cliente",
      dataIndex: "customer_name",
    },

    {
      title: "Teléfono",
      dataIndex: "customer_phone",
      render: (value) => value || "-",
    },

    {
      title: "Entrega",
      dataIndex: "delivery_date",
      render: (value) => value || "Sin fecha",
    },

    {
      title: "Piezas",
      dataIndex: "total_items",
    },

    {
      title: "Total",
      dataIndex: "total",
      render: formatMoney,
    },

    {
      title: "Anticipo",
      dataIndex: "deposit",
      render: formatMoney,
    },

    {
      title: "Restante",
      dataIndex: "remaining",
      render: formatMoney,
    },

    {
      title: "Estado",
      dataIndex: "status",
      render: (status) => {
        const { label, color } = getStatusConfig(status);

        return <Tag color={color}>{label}</Tag>;
      },
    },

    {
      title: "Acciones",

      render: (_, order) =>
        order.status === "pending" ? (
          <Space>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => openDeliverModal(order)}
            >
              Entregar
            </Button>

            <Button
              danger
              icon={<CloseOutlined />}
              onClick={() => cancelOrder(order)}
            >
              Cancelar
            </Button>
          </Space>
        ) : null,
    },
  ];

  return (
    <>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={orders}
        scroll={{ x: 900 }}
        expandable={{
          expandedRowRender: (order) => (
            <Space wrap>
              {(order.items || []).map((item) => (
                <Tag key={item.product_id}>
                  {item.product_name ||
                    PRODUCT_NAMES[item.product_id]}
                  : {item.quantity}
                </Tag>
              ))}
            </Space>
          ),
        }}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={
          selectedOrder
            ? `Entregar pedido #${selectedOrder.id}`
            : "Entregar pedido"
        }
        open={deliverModalOpen}
        onCancel={closeDeliverModal}
        confirmLoading={delivering}
        okButtonProps={{
          disabled: !canDeliver,
        }}
        onOk={confirmDelivery}
        okText="Marcar entregado"
        cancelText="Cerrar"
      >
        {selectedOrder && (
          <Space
            orientation="vertical"
            size={16}
            style={{ width: "100%" }}
          >
            <div>
              <Text strong>
                Cliente: {selectedOrder.customer_name}
              </Text>
            </div>

            <Statistic
              title="Total del pedido"
              prefix="$"
              value={Number(selectedOrder.total || 0)}
              precision={2}
            />

            <Statistic
              title="Anticipo"
              prefix="$"
              value={Number(selectedOrder.deposit || 0)}
              precision={2}
            />

            <Statistic
              title="Restante por pagar"
              prefix="$"
              value={remaining}
              precision={2}
            />

            <div>
              <Text strong>Efectivo recibido</Text>

              <InputNumber
                prefix="$"
                min={0}
                value={payment}
                precision={2}
                controls={false}
                size="large"
                onChange={(value) =>
                  setPayment(Number(value || 0))
                }
                style={{
                  width: "100%",
                  marginTop: 8,
                }}
              />

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 10,
                }}
              >
                {[15,20,25, 50, 100, 200, 500, 1000].map(
                  (amount) => (
                    <Button
                      key={amount}
                      onClick={() => setPayment(amount)}
                    >
                      ${amount}
                    </Button>
                  )
                )}
              </div>
            </div>

            {payment > 0 && payment < remaining && (
              <Text type="danger">
                Faltan{" "}
                {formatMoney(remaining - payment)}
              </Text>
            )}

            <Statistic
              title="Cambio"
              prefix="$"
              value={change}
              precision={2}
              valueStyle={{ fontSize: 32 }}
            />
          </Space>
        )}
      </Modal>
    </>
  );
}